import { useState } from 'react';

import { useAppSelector } from 'flow/hooks/useRedux';
import { selectOwnUserHasTicketPermission } from 'flow/selectors/ticket-permission-selectors';
import { TicketKey } from 'flow/types/EntityKeys';
import {
    getReplyChannelForOrigin,
    originOpensOnReply,
} from 'flow/utils/ticket-channel-utils';

export type ComposerTab = 'internal' | 'reply_external';

const getDefaultTab = (originSubtype?: string): ComposerTab =>
    originOpensOnReply(originSubtype) ? 'reply_external' : 'internal';

interface Params {
    ticketKey: TicketKey;
    originSubtype?: string;
}

/**
 * Which composer tab is in effect, and how to pick another.
 *
 * The picked tab is held in here rather than returned, so a caller only ever has the
 * tab that is actually in effect — there is no second, nearly identically named
 * value to reach for by mistake.
 *
 * Either tab can be refused. Non-members may post an internal note but never reply
 * to the bPart, and a member without the tickets licence may do neither. A refusal
 * can also arrive after mount: a ticket's permissions come with the ticket, and a
 * viewer can be removed from it. So the tab is clamped on read rather than corrected
 * by an effect, which would render a composer once before taking it away again.
 *
 * When neither tab is permitted the picked one stays in effect, and
 * `tabSelectedIsWritable` says the composer must be held shut.
 *
 * Reply is also refused until the origin is known (`originPending`), and for an
 * origin with no reply channel (`replyUnsupported`).
 */
export const useTicketComposerTab = ({ ticketKey, originSubtype }: Params) => {
    const [tabPicked, setTabPicked] = useState<ComposerTab>(
        getDefaultTab(originSubtype),
    );
    const [synced, setSynced] = useState({ ticketKey, originSubtype });

    const originPending = !originSubtype;
    const replyUnsupported =
        !originPending && !getReplyChannelForOrigin(originSubtype);
    const canReply =
        useAppSelector((state) =>
            selectOwnUserHasTicketPermission(state, ticketKey, 'reply'),
        ) &&
        !originPending &&
        !replyUnsupported;
    const canAddInternalNote = useAppSelector((state) =>
        selectOwnUserHasTicketPermission(state, ticketKey, 'addInternalNote'),
    );

    // The composer is not remounted between tickets, so the initializer above runs
    // only once. Each ticket should open on its own default tab, and a switch made
    // within a ticket should survive.
    //
    // Both keys are needed. ticketKey alone would miss the origin arriving late —
    // it is read off the ticket, so it is undefined until that is in the store, and
    // an absent origin defaults to the internal tab; a WhatsApp ticket would open
    // on the wrong one and never be corrected. originSubtype alone would miss moving
    // between two tickets of the same origin.
    //
    // Done during render rather than in an effect: React throws this render away and
    // redoes it before painting, where an effect runs after paint and would show the
    // previous ticket's tab first.
    if (
        synced.ticketKey !== ticketKey ||
        synced.originSubtype !== originSubtype
    ) {
        setSynced({ ticketKey, originSubtype });
        setTabPicked(getDefaultTab(originSubtype));
    }

    const getTabSelected = (): ComposerTab => {
        if (tabPicked === 'reply_external') {
            return canReply ? 'reply_external' : 'internal';
        }
        if (canAddInternalNote) return 'internal';

        return canReply ? 'reply_external' : 'internal';
    };

    const tabSelected = getTabSelected();

    return {
        tabSelected,
        setTabSelected: setTabPicked,
        tabSelectedIsWritable:
            tabSelected === 'internal' ? canAddInternalNote : canReply,
        canReply,
        canAddInternalNote,
        originPending,
        replyUnsupported,
    };
};
